import {BaseStoreItem} from '../../../../shared/store/BaseStoreItem'
import {createDivIn, createHeaderIn, createImgIn, createLinkIn, createParagraphIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {ReferralReward, ReferralProgram} from '../../../../shared/ReferralProgram'
import {LobbyImage} from '@colonist/types'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {clientI18n} from '../ClientI18n'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {audioManager, AudioType} from '../Sound'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {ClientUserState} from '../ClientUserState'
import {UIStoreBodyController} from './UIStoreBodyController'
import {ABTestCategory} from '../../../../shared/ABTestTypes'

export class UIStoreReferralController {
    protected readonly referralSection: HTMLDivElement
    protected readonly referralItemsDiv: HTMLDivElement
    protected readonly referralInfoDiv: HTMLDivElement
    protected readonly referralSidebarButton: HTMLDivElement
    protected readonly referralTabMobile: HTMLDivElement
    private readonly storeBodyController: UIStoreBodyController

    constructor(storeBodyController: UIStoreBodyController) {
        this.storeBodyController = storeBodyController
        this.referralSection = (document.getElementById('referrals') ?? document.querySelector('.m-store-referral')) as HTMLDivElement
        this.referralItemsDiv = document.getElementById('store_page_referral_items') as HTMLDivElement
        this.referralInfoDiv = document.getElementById('store_page_referral_info') as HTMLDivElement
        this.referralSidebarButton = document.getElementById('referral_sidebar_button') as HTMLDivElement
        this.referralTabMobile = document.getElementById('m-store-button-rewards') as HTMLDivElement
    }

    async updateReferralProgram(assignABTest: boolean) {
        if(!await UIStoreReferralController.isReferralProgramActive(assignABTest)) return

        if(this.referralSection != undefined) this.referralSection.classList.remove('hidden')
        if(this.referralSidebarButton != undefined) this.referralSidebarButton.classList.remove('hidden')
        if(this.referralTabMobile != undefined) this.referralTabMobile.classList.remove('hidden')

        const userReferralCount = await UIStoreReferralController.getUserReferralCount()

        this.referralInfoDiv.replaceChildren()
        this.referralItemsDiv.replaceChildren()

        createParagraphIn(this.referralInfoDiv, {key: 'strings:storePage.sections.referral.description', options: {referralCount: userReferralCount, context: userReferralCount == 1 ? 'one' : 'other'}}, '', '')

        this.loadReferralProgramDetails(userReferralCount)
    }

    static async isReferralProgramActive(assignABTest: boolean = true): Promise<boolean> {
        if(UIUtilsEjsVariables.isUserOnDiscord) return true

        if(UIStoreReferralController.isReferralProgramAssigned()) {
            return UIStoreReferralController.isUserInActiveTestGroup(ClientUserState.abTests.REFERRAL_PROGRAM)
        }

        if(!assignABTest) return false

        const userABTestAssignment = await UIStoreReferralController.assignReferralProgramABTest()
        return UIStoreReferralController.isUserInActiveTestGroup(userABTestAssignment)
    }

    private static isReferralProgramAssigned(): boolean {
        return ClientUserState.abTests.REFERRAL_PROGRAM != undefined
    }

    private static isUserInActiveTestGroup(abTest: string | undefined): boolean {
        return abTest == 'REFERRAL_PROGRAM_ACTIVE'
    }

    private static async assignReferralProgramABTest(): Promise<string | undefined> {
        const dataToSend = {abTestCategory: 'REFERRAL_PROGRAM' as ABTestCategory} as AjaxDataTypes.AssignABTestData
        const response = await UIAjaxWrapper.postRequestWhenReady(UIAPIEndpoints.apiAssignABTest(), undefined, dataToSend)
        return response?.json?.abTestId
    }

    private static async getUserReferralCount(): Promise<number> {
        if(ClientUserState.userState?.id == undefined) return 0
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetUserReferralCount(), undefined)
        return data?.json?.referralCount ?? 0
    }

    private createBaseView(): {contentDiv: HTMLDivElement, headerDiv: HTMLDivElement, bodyDiv: HTMLDivElement} {
        const contentDiv = createDivIn(this.referralItemsDiv, 'store_page_item')
        const headerDiv = createDivIn(contentDiv, 'store_page_item_header')
        const bodyDiv = createDivIn(contentDiv, 'store_page_item_body')
        return {contentDiv: contentDiv, headerDiv: headerDiv, bodyDiv: bodyDiv}
    }

    private static createButton(contentDiv: HTMLDivElement, referralReward: ReferralReward, userReferralCount: number): HTMLLinkElement {
        const button = createLinkIn(contentDiv, '#', {key: 'strings:colonistCommon.utils.emptyString'}, 'store_button')
        const buttonText = UIStoreReferralController.isRewardAlreadyUnlocked(userReferralCount, referralReward.referralCount) ? UIStoreReferralController.markAsUnlocked(button, contentDiv) : UIStoreReferralController.markAsToBeUnlocked(button, referralReward.referralCount)
        return button
    }

    private static createTitleAndImage(headerDiv: HTMLDivElement, bodyDiv: HTMLDivElement, referralReward: ReferralReward): HTMLImageElement {
        const title = createHeaderIn(headerDiv, referralReward.name, '', '', 'H3')
        if(!UIUtilsEjsVariables.isUserOnMobile) createImgIn(title, LobbyImage.InfoIcon)
        const image = createImgIn(bodyDiv, referralReward.image)
        return image
    }

    private static createDescription(bodyDiv: HTMLDivElement, referralReward: ReferralReward): HTMLParagraphElement {
        const description = createParagraphIn(bodyDiv, {key: 'strings:colonistCommon.utils.emptyString'}, '')
        description.style.display = 'none'
        setElementInnerHTML(description, {key: 'strings:colonistCommon.utils.#buffer', options: {value: UIStoreReferralController.loadReferralItemDescription(referralReward)}})
        return description
    }

    protected addReferralButtonOnClickBehavior(button: HTMLLinkElement, userReferralCount: number, referralReward: ReferralReward) {
        setElementOnClick(button, () => {
            if(UIStoreReferralController.isRewardAlreadyUnlocked(userReferralCount, referralReward.referralCount)) return
            audioManager.play(AudioType.Click)

            void this.storeBodyController.onInviteFriendsButtonClick(referralReward, userReferralCount)
        }, true)
    }

    static loadReferralItemDescription(referralReward: ReferralReward): string {
        let description = clientI18n.t('strings:storePage.sections.referral.details', {context: referralReward.possibleRewards.length === 1 ? 'one' : 'many'}) + '<br>'
        referralReward.possibleRewards.forEach((possibleReward: BaseStoreItem) => {
            description += `<br>- ${clientI18n.t(possibleReward.name.key)}`
        })
        return description
    }

    private static markAsToBeUnlocked(button: HTMLLinkElement, referralCount: number) {
        createParagraphIn(button, {key: 'strings:storePage.sections.referral.action.toBeUnlocked', options: {referralCount: referralCount, context: referralCount == 1 ? 'one' : 'other'}}, '', '')
    }

    private static markAsUnlocked(button: HTMLLinkElement, contentDiv: HTMLDivElement) {
        createParagraphIn(button, {key: 'strings:storePage.sections.referral.action.unlocked'}, '', '')
        contentDiv.className = 'store_page_item item_owned'
    }

    private static isRewardAlreadyUnlocked(userReferralCount: number, rewardReferralCount: number): boolean {
        if(userReferralCount >= rewardReferralCount) return true
        return false
    }

    static displayDescriptionInsteadOfImage(description: HTMLParagraphElement, image: HTMLImageElement) {
        description.style.display = 'block'
        image.style.display = 'none'
    }

    static displayImageInsteadOfDescription(description: HTMLParagraphElement, image: HTMLImageElement) {
        description.style.display = 'none'
        image.style.display = 'block'
    }

    static isDescriptionAlreadyDisplayed(description: HTMLParagraphElement): boolean {
        return description.style.display == 'block'
    }

    static isClickOnTheItem(event: MouseEvent, contentDiv: HTMLDivElement): boolean {
        return contentDiv.contains(event.target as Node)
    }

    private loadReferralProgramDetails(userReferralCount: number) {
        const referralProgram = ReferralProgram.referralRewardLevels
        referralProgram.forEach((referralReward: ReferralReward) => {
            const {contentDiv, headerDiv, bodyDiv} = this.createBaseView()
            const button = UIStoreReferralController.createButton(contentDiv, referralReward, userReferralCount)
            const image = UIStoreReferralController.createTitleAndImage(headerDiv, bodyDiv, referralReward)
            const description = UIStoreReferralController.createDescription(bodyDiv, referralReward)
            const clickableDiv = UIUtilsEjsVariables.isUserOnMobile ? bodyDiv : contentDiv
            this.storeBodyController.setupReferralRewardClickHandler(clickableDiv, description, image, referralReward)
            this.addReferralButtonOnClickBehavior(button, userReferralCount, referralReward)
        })
    }
}
